/**
 * @voltxampere/spice — Ground codec (synthetic)
 *
 * SPICE has no `G` ground element — `0` is the implicit reference
 * net. Schema however models ground as a *component* (one or more
 * symbols dropped on the canvas, each connected to net `gnd`).
 *
 * Sprint 16 strategy:
 *   - Import: `from-spice` does not call this codec via the
 *     dispatch table because no SPICE letter triggers it. Instead
 *     `from-spice` post-processes — for every distinct ground
 *     reference seen on a terminal, it creates a single ground
 *     component anchored next to the gnd-rail terminal.
 *
 *     (Sprint 16 simplification: we currently rely on user-side
 *     rendering of the gnd net rail. A standalone ground
 *     component is emitted only when the user authored one in the
 *     canvas — round-trip of canvas → SPICE → canvas keeps it.)
 *
 *   - Export: `to-spice` skips ground components in its main
 *     element loop because SPICE doesn't take a `G` element.
 *     Wires connecting to gnd already carry the `0` net name via
 *     `netToSpiceName`, so the import side reconstructs ground
 *     placement from the net topology without needing an explicit
 *     element on the wire.
 *
 *   - The `parse` method is provided for symmetry but never
 *     dispatched (SPICE letter is empty); it always returns an
 *     error. The `emit` method returns an empty string — the
 *     orchestrator detects this and writes a comment instead so
 *     the round-trip leaves a breadcrumb that the user placed a
 *     ground component there.
 *
 * Schema mapping: `type: "ground"`, params: `{}`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { err } from "./_base.js";
export const groundCodec = {
    // Empty letter — never dispatched from `from-spice`.
    spiceLetter: "",
    schemaTypes: ["ground"],
    examples: ["* ground @vxa-gnd GND1 net=0"],
    parse(_card, _ctx) {
        return err("Ground has no SPICE element form");
    },
    emit(component, terminals) {
        // Comment-only emission so the round-trip preserves the
        // ground placement without polluting the SPICE deck.
        const [t0] = terminals;
        return `* ground @vxa-gnd ${component.id} net=${t0 ?? "0"}`;
    },
    owns(schemaType) {
        return schemaType === "ground";
    },
};
//# sourceMappingURL=ground.js.map